import { useEffect, useRef, useState, type ReactNode } from 'react';
import { PlanTakeover } from '@/components/studio/plan/PlanTakeover';
import { PlanWorking } from '@/components/studio/plan/PlanWorking';
import { useStudio } from '@/components/studio/studioHooks';
import { useEmbeddedIvyChatStore } from '@/stores/embeddedIvyChatStore';
import { takeHandOffFrom, type FrontDoorStart } from '@/lib/frontDoor/handoff';
import type { UIMessage } from '@/lib/ivyChat/messageParser';
import { planFor, STARTING } from '@/lib/frontDoor/plan';
import { interactiveCostNote, lookFromTags, planProgress, takeoverAsked, takeoverHolds, takeoverPrompt, takeoverReply, takeoverStatus } from '@/lib/studioB/takeover';

export interface Takeover {
  /** The words the video was started with, tags and all, when it was started from this tab. */
  sent?: string;
  /** What a front-door start handed over: the idea and the look /start showed. */
  frontDoor?: FrontDoorStart;
}

/** How long the view may wait for a message to go out before the composer replaces "working", so nobody is left unable to type. */
const IDLE_MS = 8000;

interface Props {
  takeover: Takeover;
  /** Ivy waits on an answer the takeover cannot ask for (a choice or a checkpoint). */
  waiting: boolean;
  chat: ReactNode;
  composer: ReactNode;
  /** The chat has moved to the rail beside the storyboard or the studio: from here on it is the thread. */
  docked?: boolean;
}

/**
 * Direction B (beta only): before the outline exists, the studio picks up /start's view and keeps it while Ivy works,
 * with her latest answer as its reply. It hands over to the chat, for good, when she needs a choice or gives an answer
 * one line cannot carry.
 */
export function TakeoverOrChat({ takeover, waiting, chat, composer, docked }: Props) {
  const messages = useEmbeddedIvyChatStore((s) => s.messages);
  const isStreaming = useEmbeddedIvyChatStore((s) => s.isStreaming);
  const holding = useRef(true);
  const reply = takeoverReply(messages);
  const answered = !!reply;
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    setIdle(false);
    if (isStreaming || answered) return;
    const timer = setTimeout(() => setIdle(true), IDLE_MS);
    return () => clearTimeout(timer);
  }, [isStreaming, answered]);
  if (holding.current && (docked || !takeoverHolds({ messages, isStreaming, waiting }))) holding.current = false;
  if (!holding.current) return <>{chat}</>;
  return <TakeoverView takeover={takeover} messages={messages} isStreaming={isStreaming} idle={idle} composer={composer} />;
}

/** The frame the takeover opens on, painted while Ivy's chat is still loading so the cut from /start has no gap. */
export function TakeoverOpening({ takeover }: { takeover: Takeover }) {
  return <TakeoverView takeover={takeover} messages={[]} isStreaming={false} idle={false} composer={null} />;
}

interface ViewProps {
  takeover: Takeover;
  messages: UIMessage[];
  isStreaming: boolean;
  idle: boolean;
  composer: ReactNode;
}

function TakeoverView({ takeover, messages, isStreaming, idle, composer }: ViewProps) {
  const reply = takeoverReply(messages);
  const asked = takeoverAsked(messages);
  // She finished or was stopped, with or without saying anything: nothing here may still look like work.
  const over = idle || (asked && !isStreaming);
  const prompt = takeoverPrompt(takeover.sent, messages);
  const tags = takeover.sent ?? messages.find((m) => m.type === 'chat' && m.role === 'user')?.content ?? '';
  const plan = planFor({ prompt, chip: takeover.frontDoor?.chip ?? null, look: takeover.frontDoor?.look ?? lookFromTags(tags) });
  const studio = useStudio();
  const titles = studio ? [...studio.scenes].sort((a, b) => a.order - b.order).map((s) => s.title) : [];
  const shown = planProgress(plan, studio?.stage ?? 'plan', titles);
  // Sent from the home box: the words fly in from it. From /start (whose box is gone) the view is already on screen.
  const [from] = useState(takeHandOffFrom);
  return (
    <div data-studio-takeover data-clarity-mask="True" className="h-full min-h-0 overflow-y-auto">
      <PlanTakeover
        prompt={prompt}
        status={over ? null : takeoverStatus(messages, isStreaming, STARTING)}
        working={!over}
        reply={reply?.text ?? plan.reply}
        note={interactiveCostNote(messages)}
        steps={shown.steps}
        scenes={shown.scenes}
        from={from}
        still={!!takeover.frontDoor && !from}
        footer={asked || reply || idle ? <div className="-mx-3 -mb-3 -mt-2">{composer}</div> : <PlanWorking />}
      />
    </div>
  );
}
